import { format, isValid } from "date-fns";

import { ChartNote } from "services/types/chart-notes.type";
import {
  Appointment,
  AppointmentResponse
} from "services/types/appointments.type";
import { convertAppointmentResponseToModel } from "./appointments.util";
import { formatClinic } from "./clinics.util";
import { formatStaffMemberFullName, getHtmlAsTextPreview } from "./common";
import { formatPatient } from "./patients.util";
import { CHART_NOTE_COMPLETED } from "constants/chart-notes";
import { WORD_PROCESSOR_DELIVERED_AT_DATE } from "constants/common";
import { getWordProcessorInitialText } from "components/word-processor-field/word-processor-value.utils";

const CHART_NOTE_FILE_DATE_FORMAT = "yyyy-MM-dd";

const toValidDate = (value?: string | Date | null) => {
  if (value == null || String(value).trim() === "") {
    return null;
  }
  const parsed = value instanceof Date ? value : new Date(value);
  return isValid(parsed) ? parsed : null;
};

export const getChartNoteFileName = (note: ChartNote) => {
  const visitDate =
    toValidDate((note.appointment as Appointment)?.startDate) ??
    toValidDate(note.created_at);
  const firstName = note.patient?.first_name?.replace(/[^a-zA-Z]/g, "");
  const lastName = note.patient?.last_name?.replace(/[^a-zA-Z]/g, "");
  const fileDate = visitDate
    ? `-${format(visitDate, CHART_NOTE_FILE_DATE_FORMAT)}`
    : "";

  return `${firstName}-${lastName}-chart-note${fileDate}`;
};

export const formatChartNote = ({
  appointment,
  patient,
  clinic,
  created_by,
  updated_by,
  text,
  ...chartNote
}: ChartNote): ChartNote => {
  const isWordProcessorValue =
    new Date(appointment?.created_at || chartNote.created_at) >
    WORD_PROCESSOR_DELIVERED_AT_DATE;

  const chartNoteText = isWordProcessorValue
    ? text
    : getWordProcessorInitialText(text);

  return {
    ...chartNote,
    text: chartNoteText,
    textPreview: getHtmlAsTextPreview(
      chartNoteText,
      chartNote.status === CHART_NOTE_COMPLETED ? "" : "Please Complete the Chart Note"
    ),
    patient: patient && formatPatient(patient),
    clinic: clinic && formatClinic(clinic),
    created_by: created_by && formatStaffMemberFullName(created_by),
    updated_by: updated_by && formatStaffMemberFullName(updated_by),
    appointment:
      appointment &&
      convertAppointmentResponseToModel(appointment as AppointmentResponse),
  };
};
